import type {
  ContextShape,
  ScopedDiff,
  EnhancedScopeData,
} from '@ardoq/data-model';
import {
  APIComponentAttributes,
  APIFieldType,
  ArdoqId,
  DiffMode,
  LinkedComponent,
} from '@ardoq/api-types';
import { dispatchAction } from '@ardoq/rxbeach';
import { updateViewSettings } from '@ardoq/view-settings';
import {
  type BubbleChartDataPointModel,
  BubbleChartSelectedFieldInfo,
  BubbleChartViewInterface,
  type BubbleChartViewSettings,
  type ComponentDisplayColors,
  ComponentTypeRegistryValue,
  BubbleChartFieldNotifiers,
  BubbleChartSelectedFieldType,
  SelectedFieldValuePresenceData,
  isBubbleChartFieldInfo,
  resolveIncludeAllDescendants,
  safeViewNodeDimensions,
  FieldData,
} from '@ardoq/bubble-chart';
import { isNil } from 'lodash';
import { simpleNotifier } from '@ardoq/dropdown-menu';
import { componentOperations, toIds } from '@ardoq/core';
import { isTruthy } from '@ardoq/common-helpers';
import type { ComponentsState } from 'streams/components/components$';
import type { LinkedComponentWithReferenceTypeName } from './referenceBubbleChart/viewModel$';
import type { ReferenceWithBubbleComponent } from './referenceBubbleChart/types';
import { PermissionContext } from '@ardoq/access-control';
import {
  componentUtils,
  enhancedScopeDataOperations,
  fieldUtils,
  type FilterColorData,
} from '@ardoq/scope-data';
import { enhanceAttributesWithPermissions } from 'streams/fields/getFieldsForViewpoint';

const isComponentFieldValueCompliant = (
  componentFieldValue: unknown
): boolean => {
  if (isNil(componentFieldValue)) {
    return false;
  }

  return (
    typeof componentFieldValue === 'number' && isFinite(componentFieldValue)
  );
};

/**
 * If a component has no field or no field value for any of the selected fields,
 * we filter it out from the view data.
 *
 * If no field is selected for a dimension, we do not filter by that dimension.
 */
export const isComponentCompliantToSelectedField = (
  fieldName: string,
  componentId: ArdoqId,
  viewInterface: BubbleChartViewInterface
) => {
  if (!fieldName) {
    return true;
  }
  const componentFieldValue = viewInterface.getComponentFieldValue(
    componentId,
    fieldName
  );

  return isComponentFieldValueCompliant(componentFieldValue);
};

const isComponentCompliantToSelectedFieldWithScopeData = (
  fieldName: string,
  componentId: ArdoqId,
  scopeData: EnhancedScopeData
) => {
  if (!fieldName) {
    return true;
  }
  const componentFieldValue = fieldUtils.getFieldValueForComponentOrReference(
    scopeData,
    componentId,
    fieldName
  );

  return isComponentFieldValueCompliant(componentFieldValue);
};

export const isCompliantToSelectedFields = (
  scopeData: EnhancedScopeData,
  componentId: ArdoqId,
  xFieldName: string,
  yFieldName: string,
  radiusFieldName: string
): boolean => {
  const isFieldXCompliant = isComponentCompliantToSelectedFieldWithScopeData(
    xFieldName,
    componentId,
    scopeData
  );

  const isFieldYCompliant = isComponentCompliantToSelectedFieldWithScopeData(
    yFieldName,
    componentId,
    scopeData
  );

  const isRadiusFieldCompliant =
    isComponentCompliantToSelectedFieldWithScopeData(
      radiusFieldName,
      componentId,
      scopeData
    );

  return isFieldXCompliant && isFieldYCompliant && isRadiusFieldCompliant;
};

const collectFieldPresenceData = <T>(
  items: T[],
  xFieldChecker: (item: T) => boolean,
  yFieldChecker: (item: T) => boolean,
  radiusFieldChecker: (item: T) => boolean
): SelectedFieldValuePresenceData => {
  let hasXValues = false;
  let hasYValues = false;
  let hasRadiusValues = false;

  for (const item of items) {
    if (!hasXValues && xFieldChecker(item)) {
      hasXValues = true;
    }
    if (!hasYValues && yFieldChecker(item)) {
      hasYValues = true;
    }
    if (!hasRadiusValues && radiusFieldChecker(item)) {
      hasRadiusValues = true;
    }

    // Early exit when all fields have been found
    if (hasXValues && hasYValues && hasRadiusValues) {
      break;
    }
  }

  return {
    xHasValues: hasXValues,
    yHasValues: hasYValues,
    radiusHasValues: hasRadiusValues,
  };
};

export const getSelectedFieldPresenceData = (
  viewInterface: BubbleChartViewInterface,
  componentIds: ArdoqId[],
  xFieldName: string,
  yFieldName: string,
  radiusFieldName: string
): SelectedFieldValuePresenceData =>
  collectFieldPresenceData(
    componentIds,
    componentId =>
      isComponentCompliantToSelectedField(
        xFieldName,
        componentId,
        viewInterface
      ),
    componentId =>
      isComponentCompliantToSelectedField(
        yFieldName,
        componentId,
        viewInterface
      ),
    componentId =>
      isComponentCompliantToSelectedField(
        radiusFieldName,
        componentId,
        viewInterface
      )
  );

export const getSelectedFieldPresenceDataWithScopeData = (
  scopeData: EnhancedScopeData,
  componentIds: ArdoqId[],
  xFieldName: string,
  yFieldName: string,
  radiusFieldName: string
): SelectedFieldValuePresenceData =>
  collectFieldPresenceData(
    componentIds,
    componentId =>
      isComponentCompliantToSelectedFieldWithScopeData(
        xFieldName,
        componentId,
        scopeData
      ),
    componentId =>
      isComponentCompliantToSelectedFieldWithScopeData(
        yFieldName,
        componentId,
        scopeData
      ),
    componentId =>
      isComponentCompliantToSelectedFieldWithScopeData(
        radiusFieldName,
        componentId,
        scopeData
      )
  );

export const selectedFieldValue = (
  viewInterface: BubbleChartViewInterface,
  { referenceId, componentId }: LinkedComponent,
  { fieldName, type }: BubbleChartSelectedFieldInfo
) =>
  type === BubbleChartSelectedFieldType.REFERENCE
    ? viewInterface.getReferenceFieldValue(referenceId, fieldName)
    : viewInterface.getComponentFieldValue(componentId, fieldName);

const toFieldOwnerId = (
  { bubbleComponentId, referenceId, componentId }: ReferenceWithBubbleComponent,
  type: BubbleChartSelectedFieldType
): ArdoqId => {
  switch (type) {
    case BubbleChartSelectedFieldType.BUBBLE_COMPONENT:
      return bubbleComponentId;
    case BubbleChartSelectedFieldType.REFERENCE:
      return referenceId;
    case BubbleChartSelectedFieldType.REFERENCED_COMPONENT:
      return componentId;
  }
};

export const selectedFieldValueWithScopeData = (
  enhancedScopeData: EnhancedScopeData,
  reference: ReferenceWithBubbleComponent,
  { fieldName, type }: BubbleChartSelectedFieldInfo
): ReturnType<typeof fieldUtils.getFieldValueForComponentOrReference> =>
  fieldUtils.getFieldValueForComponentOrReference(
    enhancedScopeData,
    toFieldOwnerId(reference, type),
    fieldName
  );

const isReferenceCompliantToSelectedField = (
  viewInterface: BubbleChartViewInterface,
  reference: LinkedComponentWithReferenceTypeName,
  selectedField: string | BubbleChartSelectedFieldInfo | null
): boolean => {
  if (!isBubbleChartFieldInfo(selectedField)) {
    return true;
  }

  const value = selectedFieldValue(viewInterface, reference, selectedField);

  if (isNil(value)) {
    return false;
  }

  return typeof value === 'number' && isFinite(value);
};

const isReferenceCompliantToSelectedFieldWithScopeData = (
  scopeData: EnhancedScopeData,
  reference: ReferenceWithBubbleComponent,
  selectedField: string | BubbleChartSelectedFieldInfo | null
): boolean => {
  if (!isBubbleChartFieldInfo(selectedField)) return true;

  const value = selectedFieldValueWithScopeData(
    scopeData,
    reference,
    selectedField
  );

  if (isNil(value)) {
    return false;
  }

  return typeof value === 'number' && isFinite(value);
};

export const getReferenceFieldPresenceData = (
  viewInterface: BubbleChartViewInterface,
  usedReferences: LinkedComponentWithReferenceTypeName[],
  selectedFieldNameX: string | BubbleChartSelectedFieldInfo | null,
  selectedFieldNameY: string | BubbleChartSelectedFieldInfo | null,
  selectedFieldNameRadius: string | BubbleChartSelectedFieldInfo | null
): SelectedFieldValuePresenceData =>
  collectFieldPresenceData(
    usedReferences,
    reference =>
      isReferenceCompliantToSelectedField(
        viewInterface,
        reference,
        selectedFieldNameX
      ),
    reference =>
      isReferenceCompliantToSelectedField(
        viewInterface,
        reference,
        selectedFieldNameY
      ),
    reference =>
      isReferenceCompliantToSelectedField(
        viewInterface,
        reference,
        selectedFieldNameRadius
      )
  );

export const getReferenceFieldPresenceDataWithScopeData = (
  scopeData: EnhancedScopeData,
  usedReferences: ReferenceWithBubbleComponent[],
  selectedFieldNameX: string | BubbleChartSelectedFieldInfo | null,
  selectedFieldNameY: string | BubbleChartSelectedFieldInfo | null,
  selectedFieldNameRadius: string | BubbleChartSelectedFieldInfo | null
): SelectedFieldValuePresenceData =>
  collectFieldPresenceData(
    usedReferences,
    reference =>
      isReferenceCompliantToSelectedFieldWithScopeData(
        scopeData,
        reference,
        selectedFieldNameX
      ),
    reference =>
      isReferenceCompliantToSelectedFieldWithScopeData(
        scopeData,
        reference,
        selectedFieldNameY
      ),
    reference =>
      isReferenceCompliantToSelectedFieldWithScopeData(
        scopeData,
        reference,
        selectedFieldNameRadius
      )
  );

/**
 * Filters components by selected fields and context filters
 */
export const filterComponents = (
  viewInterface: BubbleChartViewInterface,
  componentIds: ArdoqId[],
  xFieldName: string,
  yFieldName: string,
  radiusFieldName: string
): ArdoqId[] => {
  return componentIds.filter(componentId => {
    const isFieldXCompliant = isComponentCompliantToSelectedField(
      xFieldName,
      componentId,
      viewInterface
    );
    const isFieldYCompliant = isComponentCompliantToSelectedField(
      yFieldName,
      componentId,
      viewInterface
    );
    const isRadiusFieldCompliant = isComponentCompliantToSelectedField(
      radiusFieldName,
      componentId,
      viewInterface
    );

    const component = viewInterface.get(componentId);
    return (
      isFieldXCompliant &&
      isFieldYCompliant &&
      isRadiusFieldCompliant &&
      (!component ||
        viewInterface.isComponentIncludedInContextByFilter(component))
    );
  });
};

type AllComponentsResult = {
  components: ArdoqId[];
  hasComponentsAvailable: boolean;
};

const subtreeIds = (
  root: APIComponentAttributes,
  childrenByParent: Map<ArdoqId, APIComponentAttributes[]>
): ArdoqId[] =>
  toIds(componentOperations.getFlatHierarchy([root], childrenByParent));

export const getAllComponents = (
  components: ComponentsState,
  { workspaceId, componentId }: ContextShape,
  includeAllDescendants: BubbleChartViewSettings['includeAllDescendants']
): AllComponentsResult => {
  const resolvedIncludeAllDescendants = resolveIncludeAllDescendants(
    includeAllDescendants
  );

  if (!workspaceId) {
    return { components: [], hasComponentsAvailable: false };
  }

  const contextRoots = componentId
    ? [components.byId[componentId]].filter(isTruthy)
    : componentOperations.getRootComponents(workspaceId, components.models);

  const resolvedComponents =
    resolvedIncludeAllDescendants === 'none'
      ? toIds(contextRoots)
      : resolvedIncludeAllDescendants === 'all'
        ? contextRoots.flatMap(root => {
            const allIds = subtreeIds(root, components.childrenByParent);
            // When a context component is selected we exclude it from the result;
            // when iterating workspace roots we keep them.
            return componentId
              ? allIds.filter(id => id !== componentId)
              : allIds;
          })
        : contextRoots.flatMap(root => {
            const childIds = toIds(
              components.childrenByParent.get(root._id) ?? []
            );
            if (!componentId) {
              return [root._id, ...childIds];
            }
            return childIds.length > 0 ? childIds : [root._id];
          });

  return {
    components: resolvedComponents,
    hasComponentsAvailable: resolvedComponents.length > 0,
  };
};

type GetComponentsDataResult = {
  components: ArdoqId[];
  hasComponentsAvailable: boolean;
  emptyFields: SelectedFieldValuePresenceData;
};
export const getComponentsData = (
  components: ComponentsState,
  viewInterface: BubbleChartViewInterface,
  context: ContextShape,
  xFieldName: string,
  yFieldName: string,
  radiusFieldName: string,
  includeAllDescendants: BubbleChartViewSettings['includeAllDescendants']
): GetComponentsDataResult => {
  const { components: candidateComponents, hasComponentsAvailable } =
    getAllComponents(components, context, includeAllDescendants);

  if (!hasComponentsAvailable) {
    return {
      components: [],
      hasComponentsAvailable,
      emptyFields: {
        xHasValues: !xFieldName,
        yHasValues: !yFieldName,
        radiusHasValues: !radiusFieldName,
      },
    };
  }

  const emptyFields = getSelectedFieldPresenceData(
    viewInterface,
    candidateComponents,
    xFieldName,
    yFieldName,
    radiusFieldName
  );

  const filteredComponents = filterComponents(
    viewInterface,
    candidateComponents,
    xFieldName,
    yFieldName,
    radiusFieldName
  );

  return {
    components: filteredComponents,
    hasComponentsAvailable,
    emptyFields,
  };
};

const componentTypeRegistryReducer = (
  scopeData: EnhancedScopeData,
  registry: Map<string, ComponentTypeRegistryValue>,
  componentId: ArdoqId
) => {
  const component = scopeData.componentsById[componentId];
  if (!component) {
    return registry;
  }

  const componentType =
    enhancedScopeDataOperations.getComponentTypeByComponentId(
      scopeData,
      componentId
    );
  if (!componentType) {
    return registry;
  }

  const componentTypeId = componentType.id;
  const components = registry.get(componentTypeId)?.components;
  if (components) {
    components.add(componentId);
  } else {
    registry.set(componentTypeId, {
      componentType,
      components: new Set([componentId]),
    });
  }
  return registry;
};
export const buildComponentTypeRegistry = (
  scopeData: EnhancedScopeData,
  viewpointComponentsWithAncestors: ArdoqId[]
): Map<ArdoqId, ComponentTypeRegistryValue> => {
  return viewpointComponentsWithAncestors.reduce(
    (acc, val) => componentTypeRegistryReducer(scopeData, acc, val),
    new Map()
  );
};

export const getComponentIdsOfTypes = (
  componentTypeRegistry: Map<ArdoqId, ComponentTypeRegistryValue>,
  componentTypeIds: Set<ArdoqId>
): ArdoqId[] =>
  Array.from(componentTypeIds).flatMap(componentTypeId =>
    Array.from(componentTypeRegistry.get(componentTypeId)?.components ?? [])
  );

export const getNumericFieldsForWorkspace = (
  viewInterface: BubbleChartViewInterface,
  permissionContext: PermissionContext,
  contextWorkspaceId?: ArdoqId
): FieldData[] => {
  if (!contextWorkspaceId) {
    return [];
  }
  return viewInterface
    .getFieldsOfWorkspace(contextWorkspaceId, [APIFieldType.NUMBER])
    .map(attributes =>
      enhanceAttributesWithPermissions(
        permissionContext,
        attributes,
        contextWorkspaceId
      )
    );
};

type ComponentDataPointModelBaseArgs = {
  highlightedComponent: ArdoqId | null;
  componentId: ArdoqId;
  selectedFieldNameX: string;
  selectedFieldNameY: string;
  selectedFieldNameRadius: string;
  labelsData: Map<ArdoqId, string>;
};

type ToComponentDataPointModelArgs = ComponentDataPointModelBaseArgs & {
  viewInterface: BubbleChartViewInterface;
  displayColors: Map<ArdoqId, ComponentDisplayColors>;
};
export const toComponentDataPointModel = ({
  viewInterface,
  highlightedComponent,
  componentId,
  selectedFieldNameX,
  selectedFieldNameY,
  selectedFieldNameRadius,
  labelsData,
  displayColors,
}: ToComponentDataPointModelArgs): BubbleChartDataPointModel => {
  const selectedXValue = viewInterface.getComponentFieldValue(
    componentId,
    selectedFieldNameX
  );
  const selectedYValue = viewInterface.getComponentFieldValue(
    componentId,
    selectedFieldNameY
  );
  const selectedRadiusValue = viewInterface.getComponentFieldValue(
    componentId,
    selectedFieldNameRadius
  );
  const { fill, stroke } = displayColors.get(componentId) ?? {
    fill: '',
    stroke: '',
  };
  return {
    cid: componentId,
    uniqueId: componentId,
    ...safeViewNodeDimensions(
      selectedXValue,
      selectedYValue,
      selectedRadiusValue
    ),
    class: viewInterface.getComponentCssClassNames(componentId) ?? '',
    fill,
    stroke,
    label: labelsData.get(componentId) ?? '',
    isContext: highlightedComponent
      ? highlightedComponent === componentId
      : false,
  };
};

type ToComponentDataPointModelWithScopeDataArgs =
  ComponentDataPointModelBaseArgs & {
    enhancedScopeData: EnhancedScopeData;
    filterColorData: FilterColorData;
  };
export const toComponentDataPointModelWithScopeData = ({
  enhancedScopeData,
  filterColorData,
  highlightedComponent,
  componentId,
  selectedFieldNameX,
  selectedFieldNameY,
  selectedFieldNameRadius,
  labelsData,
}: ToComponentDataPointModelWithScopeDataArgs): BubbleChartDataPointModel => {
  const selectedXValue = fieldUtils.getFieldValueForComponentOrReference(
    enhancedScopeData,
    componentId,
    selectedFieldNameX
  );
  const selectedYValue = fieldUtils.getFieldValueForComponentOrReference(
    enhancedScopeData,
    componentId,
    selectedFieldNameY
  );
  const selectedRadiusValue = fieldUtils.getFieldValueForComponentOrReference(
    enhancedScopeData,
    componentId,
    selectedFieldNameRadius
  );
  const { fill, stroke } =
    componentUtils.getComponentDisplayColorAsSVGAttributes(
      enhancedScopeData,
      filterColorData,
      componentId,
      { useAsBackgroundStyle: false }
    );
  return {
    cid: componentId,
    uniqueId: componentId,
    ...safeViewNodeDimensions(
      selectedXValue,
      selectedYValue,
      selectedRadiusValue
    ),
    class:
      componentUtils.getComponentCSSClassNames({
        scopeData: enhancedScopeData,
        componentId,
        filterColorData,
      }) ?? '',
    fill,
    stroke,
    label: labelsData.get(componentId) ?? '',
    isContext: highlightedComponent
      ? highlightedComponent === componentId
      : false,
  };
};

export const createBubbleChartFieldNotifiers =
  (): BubbleChartFieldNotifiers => ({
    xFieldDropdown: simpleNotifier(),
    yFieldDropdown: simpleNotifier(),
    bubbleSizeFieldDropdown: simpleNotifier(),
  });

type CreateOnChangeFieldsClickHandlerArgs = {
  fieldNotifiers?: BubbleChartFieldNotifiers;
  emptyFields?: SelectedFieldValuePresenceData;
};

export const createOnChangeFieldsClickHandler = ({
  fieldNotifiers,
  emptyFields,
}: CreateOnChangeFieldsClickHandlerArgs): VoidFunction => {
  const { xHasValues, yHasValues, radiusHasValues } = emptyFields ?? {
    xHasValues: true,
    yHasValues: true,
    radiusHasValues: true,
  };

  return () => {
    if (!xHasValues) {
      fieldNotifiers?.xFieldDropdown?.notify();
    } else if (!yHasValues) {
      fieldNotifiers?.yFieldDropdown?.notify();
    } else if (!radiusHasValues) {
      fieldNotifiers?.bubbleSizeFieldDropdown?.notify();
    }
  };
};

type CreateZeroValuesBubbleHandlersArgs = {
  fieldNotifiers?: BubbleChartFieldNotifiers;
};

export const createOnChangeBubbleSizeFieldHandler =
  ({ fieldNotifiers }: CreateZeroValuesBubbleHandlersArgs): VoidFunction =>
  () => {
    fieldNotifiers?.bubbleSizeFieldDropdown?.notify();
  };

export const createOnShowSizeZeroBubblesHandler =
  (viewId: string): VoidFunction =>
  () => {
    dispatchAction(
      updateViewSettings({
        viewId,
        settings: { showBubblesWithZeroValue: true },
        persistent: true,
      })
    );
  };

export const getDiffMode = (
  scopeDiff: ScopedDiff | null,
  activeDiffMode: DiffMode
) => ({
  isScopeDiffMode: Boolean(scopeDiff),
  activeDiffMode: scopeDiff ? activeDiffMode : null,
});
